import Vue from 'vue'
import moment from 'moment'
import { createI18n } from '@/_i18n/index'
import { convertToCurrencyBRL, capitalizeFirstLetter, formatNumber, floatToPercent, convertNumberToStringNotation } from 'utils/functions'

const i18n = createI18n()

Vue.filter('myLocale', function (value, localePath) {
  return i18n.t(`${localePath}.${value}`)
})

Vue.filter('cnpj', value => {
  if (!value) return value
  // formats a number to: XX.XXX.XXX/YYYY-ZZ
  const str = value.toString().replace(/\D/g, '').split('')
  str.splice(2, 0, '.')
  str.splice(6, 0, '.')
  str.splice(10, 0, '/')
  str.splice(15, 0, '-')

  return str.join('')
})

Vue.filter('cep', value => {
  // formats a number to: xxxxx-xxx
  if (!value) return value
  const str = value.toString().split('')
  str.splice(5, 0, '-')

  return str.join('')
})

Vue.filter('phoneWithDDD', value => {
  if (!value) return value
  if (value.includes('(')) return // it's already formatted

  let str = value + ''

  if (str.length === 11) {
    str = str.replace(/^([\d*]{2})([\d*]{1})([\d*]{4})([\d*]{4})/, '($1) $2 $3-$4')
  } else {
    str = str.replace(/^([\d*]{2})([\d*]{4})([\d*]{4})/, '($1) $2-$3')
  }

  return str
})

Vue.filter('phoneWithDDI', (value, ddi = '+55') => {
  if (!value) return value

  let str = value + ''
  str = str.replace(/\D/g, '')
  if (str.length === 11) {
    str = str.replace(/^(\d{2})(\d{1})(\d{4})(\d{4})/, `${ddi} $1 $2 $3 $4`)
  } else {
    str = str.replace(/^(\d{2})(\d{4})(\d{4})/, `${ddi} $1 $2 $3`)
  }
  return str
})

Vue.filter('formatDate', function (value, format) {
  if (value) {
    return moment(value).format(format)
  }
})

Vue.filter('duration', function (start, end) {
  if (start) {
    const a = moment(start)
    const b = moment(end)
    const duration = moment.duration(b.diff(a))
    const minutes = Math.round(duration.asMinutes())
    return minutes
  }
})

Vue.filter('durationCalc', function (start, end) {
  if (start) {
    const duration = moment.duration(moment(end).diff(moment(start)))
    return moment.utc(duration.asMilliseconds()).format('HH:mm:ss')
  }
})

Vue.filter('durationCalcPerExtensive', function (start, end) {
  if (start) {
    let duration = moment.duration(moment(end).diff(moment(start)))
    duration = moment.utc(duration.asMilliseconds()).format('HH:mm:ss')
    duration = duration.split(':')
    if (duration[0] === '00') {
      return `${duration[1]} mins`
    }
    return `${duration[0]}h ${duration[1]}m`
  }
})

Vue.filter('metresToKm', function (value) {
  if (!value) return '-'
  return `${(value / 1000).toFixed(2)} km`
})

Vue.filter('currency', function (value, prefix, separator) {
  if (value) {
    const fixedValue = (value).toFixed(2).split('.').join(separator)
    return `${prefix} ${fixedValue}`
  }
})

Vue.filter('capitalizeFirstLetter', function (value) {
  if (value) {
    return capitalizeFirstLetter(value)
  }
})

Vue.filter('currencyBRL', function (value) {
  return convertToCurrencyBRL(value)
})

Vue.filter('currencyBRLwithoutSymbol', function (value) {
  return convertToCurrencyBRL(value).replace(/R\$\s/, '')
})

Vue.filter('ellipsis', function (value, chars) {
  if (!value) return ''
  if (value.length < chars) return value
  return `${value.slice(0, chars - 3)}...`
})

Vue.filter('formatNumber', function (value) {
  return formatNumber(value)
})

Vue.filter('formatCPF', function (value) {
  if (!value) return
  return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
})

Vue.filter('textLowercase', function (value) {
  if (!value) return
  return value.toLowerCase()
})

Vue.filter('trim', function (value) {
  if (!value) return
  return value.trim()
})

Vue.filter('getAddressStreet', function (value) {
  if (!value) return '-'

  const arr = value.split(/\s?,\s?/)
  const number = arr[1] ? arr[1].split(' - ') : arr[0]
  const isNumber = !isNaN(Number(number[0]))

  return isNumber ? `${arr[0]}, ${arr[1]}`.replace(/(.+)\s?-.+/, '$1') : arr[0]
})

Vue.filter('getAddressLocation', function (value) {
  if (!value) return '-'

  const arr = value.split(/\s?,\s?/)
  const arrLength = arr.length
  const removeNumber = (str) => {
    return str ? str.replace(/\d+\s-\s/, '') : '-'
  }
  const removeCep = (str) => {
    return str ? str.replace(/\d{5}-\d{3}/, '') : '-'
  }

  return arrLength > 1
    ? `${removeNumber(arr[arrLength - 3])} ${removeCep(arr[arrLength - 2])}`
    : '-'
})

Vue.filter('getStateAndCountry', function (val) {
  if (!val) return '-'

  const statesRegex = /(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)/
  const stateMatch = val.match(statesRegex)
  const state = stateMatch && stateMatch.length ? stateMatch[0] : ''

  const countryRegex = /(Brasil)/i
  const countryMatch = val.match(countryRegex)
  const country = countryMatch && countryMatch.length ? countryMatch[0] : 'Brasil'

  return state ? `${state} - ${country}` : '-'
})

Vue.filter('formatPercentage', function (val) {
  const decimalPlaces = val && Number.isInteger(val) ? 0 : 2
  return val ? floatToPercent(val, decimalPlaces) : '0%'
})

Vue.filter('convertNumberToStringNotation', function (val) {
  return convertNumberToStringNotation(val)
})
